import { BackgroundRatio } from "@/services/bedrock/dtos/BackgroundAsset";
import { DesignPackageDetail, DesignPackageItem, ItemStatus } from "@/services/bedrock/dtos/DesignPackage";

export type SlideState =
  | "active"
  | "candidate_rendering"
  | "candidate_ready"
  | "candidate_failed"
  | "approving"
  | "discarding";

export type SlideBusy = Record<number, "approving" | "discarding" | undefined>;

const IN_PROGRESS_ITEM_STATUSES: ItemStatus[] = ["queued", "generating_html", "rendering_previews"];

export function isItemInProgress(item: Pick<DesignPackageItem, "status">): boolean {
  return IN_PROGRESS_ITEM_STATUSES.includes(item.status);
}

/**
 * The shape a slide's background fills: its template's first slot ratio (a polaroid's square), else
 * the design's own ratio. The tray, the picker, uploads, and generation all use it.
 */
export function slotRatioFor(
  item: Pick<DesignPackageItem, "activeTemplate">,
  pkg: Pick<DesignPackageDetail, "aspectRatio">
): BackgroundRatio {
  return item.activeTemplate.backgroundSlotRatios[0] ?? pkg.aspectRatio;
}

export function slideStateFor(item: DesignPackageItem, busy: SlideBusy): SlideState {
  const pending = busy[item.id];
  if (pending) return pending;
  if (isItemInProgress(item)) return "candidate_rendering";
  if (item.status === "failed" && item.candidateTemplate) return "candidate_failed";
  if (item.candidateTemplate) return "candidate_ready";
  return "active";
}

/** Intro and Ending only make sense around review slides; anything else is a plain "Slide". */
export function slideKindLabel(
  pkg: Pick<DesignPackageDetail, "items">,
  item: DesignPackageItem
): "Review" | "Profile" | "Intro" | "Ending" | "Slide" {
  if (item.contextType === "review") return "Review";
  if (item.contextType === "player") return "Profile";

  const items = [...pkg.items].sort((a, b) => a.position - b.position);
  const index = items.findIndex(i => i.id === item.id);
  const isFirst = index === 0;
  const isLast = index === items.length - 1;
  if (isFirst && items.slice(1).some(i => i.contextType === "review")) return "Intro";
  if (isLast && items.slice(0, -1).some(i => i.contextType === "review")) return "Ending";
  return "Slide";
}

/** "Slide 2 · Review", or "Slide 2" when the kind adds nothing. */
export function slideTitle(position: number, kind: ReturnType<typeof slideKindLabel>): string {
  return kind === "Slide" ? `Slide ${position}` : `Slide ${position} · ${kind}`;
}
